import {property, query, queryAll} from "lit/decorators.js";
import {html, type PropertyValues, type TemplateResult} from "lit";
import {clamp} from "../../../bootstrap/common/numbers";
import {intRangeClosed} from "../../../bootstrap/common/arrays";
import {AutoInitializing, UnLitElement, WithEopSlots} from "../../../common/elements";
import {GLOBAL} from "../../../common/globals";
import {ManagingResources} from "../../../common/lifetime";
import type {Resolution} from "../../../common/resolution";
import {BOOTSTRAP} from "../../../common/resolutionConstants";
import {Page} from "../../../common/page";
import type {ScrollService} from "../../../common/scroll";
import {KEYS, prepareEvents} from "../../../common/utils/events";
import {schedule, waitTime} from "../../../common/utils/promises";
import {isDefined} from "../../../common/utils/basics";
import type {Nullable} from "../../../bootstrap/common/types";


type ExecutiveBoardGalleryConfig = {
    portraitCount: number;
    hasGroupShot: boolean;
    scrollHeight: number;
    portraitOnStageTimeline: number[];
    portraitAnimationStartTimeline: number[];
    portraitTextBoxVisibilityIntervalTimeline: [number, number][];
};

const SCROLL_HEIGHT_PER_ELEMENT = 1000; // adjust to change animation speed

export function executiveBoardGalleryConfig(portraitOnStageTimeline: number[], params: {
    portraitCount: number;
    hasGroupShot: boolean;
}): ExecutiveBoardGalleryConfig {
    // 0.02 is the end of the group shot, if present, or will be ignored otherwise
    // has (calculated) duplicate in scss
    const portraitAnimationStartTimeline = [0.02].concat(portraitOnStageTimeline.slice(0, -1)
        .map(value => value + 0.025));
    const portraitTextBoxVisibilityIntervalTimeline = portraitOnStageTimeline
        .map(value => [value - 0.08, value + 0.04] as [number, number]);
    portraitTextBoxVisibilityIntervalTimeline.getLast()![1] = 1;
    const imageCount = params.portraitCount + (params.hasGroupShot ? 1 : 0);

    return {
        portraitCount: params.portraitCount,
        hasGroupShot: params.hasGroupShot,
        scrollHeight: imageCount * SCROLL_HEIGHT_PER_ELEMENT,
        portraitOnStageTimeline: portraitOnStageTimeline,
        portraitAnimationStartTimeline: portraitAnimationStartTimeline,
        portraitTextBoxVisibilityIntervalTimeline: portraitTextBoxVisibilityIntervalTimeline
    };
}


export abstract class EopExecutiveBoardGallery extends WithEopSlots(AutoInitializing(ManagingResources(UnLitElement))) {

    @property({attribute: "intro-headline-slot-name"})
    private introHeadlineSlotName: string;
    @property({attribute: "intro-text-slot-name"})
    private introTextSlotName: string;
    @property({attribute: "group-shot-slot-name"})
    private groupShotSlotName: string;
    @query(".content-area")
    private contentArea: HTMLElement;
    @queryAll(".portrait")
    private portraits: NodeListOf<HTMLElement>;
    @queryAll(".text-box")
    private textBoxes: NodeListOf<HTMLElement>;

    protected constructor(
        private config: ExecutiveBoardGalleryConfig,
        private resolution: Resolution,
        private scrollService: ScrollService
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();

        this.resolution.onBootstrapBreakpointChange(() => this.requestUpdate(), this);
        GLOBAL.window().addEventListener("scroll", () => this.updateScrollState());

        this.style.setProperty("--desktop-scroll-height", this.config.scrollHeight + "px");
    }

    public render(): TemplateResult {
        if (this.isOnMobile()) {
            return this.renderMobile();
        } else {
            return this.renderDesktop();
        }
    }

    private renderDesktop(): TemplateResult {
        return html`
            <div class="scroll-area">
                <div class="content-area">
                    <div class="intro">
                        <div class="intro-headline">
                            ${this.resolveSlot(this.introHeadlineSlotName)}
                        </div>
                        ${this.resolveSlot(this.introTextSlotName)}
                    </div>
                    ${this.renderGroupShot()}
                    ${(this.renderDesktopPortraits())}
                    ${(this.renderDesktopPortraitTextBoxes())}
                </div>
            </div>
        `;
    }

    private renderDesktopPortraits(): TemplateResult[] {
        return intRangeClosed(1, this.config.portraitCount).map(id => this.renderDesktopPortrait(id));
    }

    private renderDesktopPortrait(id: number): TemplateResult {
        const handleScroll = (): Promise<void> => this.scrollToPortrait(id);
        return html`
            <div class="portrait-${id} portrait" @click=${handleScroll}>
                ${this.resolveSlot(`portrait-${id}`)}
            </div>
        `;
    }

    private renderDesktopPortraitTextBoxes(): TemplateResult[] {
        return intRangeClosed(1, this.config.portraitCount).map(id => this.renderDesktopPortraitTextBox(id));
    }

    private renderDesktopPortraitTextBox(id: number): TemplateResult {
        return html`
            <div class="text-box-${id} text-box reverse-background">
                ${this.resolveSlot(`text-box-${id}`)}
            </div>`;
    }

    private renderMobile(): TemplateResult {
        return html`
            <div class="scroll-area">
                <div class="content-area">
                    <div class="intro">
                        <div class="intro-headline">
                            ${this.resolveSlot(this.introHeadlineSlotName)}
                        </div>
                        ${this.renderGroupShot()}
                        ${this.resolveSlot(this.introTextSlotName)}
                    </div>
                    <eop-multi-carousel slider-content-slot-name="slider-content">
                        ${(this.renderMobilePortraits())}
                    </eop-multi-carousel>
                </div>
            </div>
        `;
    }

    private renderMobilePortraits(): TemplateResult[] {
        return intRangeClosed(1, this.config.portraitCount).map(id => this.renderMobilePortrait(id));
    }

    private renderMobilePortrait(id: number): TemplateResult {
        return html`
            <div eop-slot="slider-content">
                <div class="portrait-tile reverse-background">
                    <div class="portrait-${id} portrait">
                        ${this.resolveSlot(`portrait-${id}`)}
                    </div>
                    <div class="text-box-${id} text-box reverse-background">
                        ${this.resolveSlot(`text-box-${id}`)}
                    </div>
                </div>
            </div>
        `;
    }

    private renderGroupShot(): Nullable<TemplateResult> {
        return this.config.hasGroupShot
            ? html`
                    <div class="group-shot">
                        ${this.resolveSlot(this.groupShotSlotName)}
                    </div>
            `
            : null;
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        // Why not handle on focusin in text-boxes: Because this interferes with browser scrolling on focus, resulting in strange flickering even for non-keyboard users.
        prepareEvents(GLOBAL.bodyElement())
            .boundTo(this)
            .on("keydown", ev => schedule(this.handleTab(ev as KeyboardEvent)).as("handling-tab-event"));
    }

    protected updated(_changedProperties: PropertyValues): void {
        super.updated(_changedProperties);
        this.updateScrollState();
    }

    private async handleTab(event: KeyboardEvent): Promise<void> {
        if (this.isOnMobile()) {
            return;
        }

        if (event.key === KEYS.TAB) {
            await this.waitForBrowserToFocusNextElement();
            const activeElement = Page.activeElement();
            if (!activeElement?.hasAncestor(this)) {
                return;
            }

            const closestTextBox = activeElement
                ?.closestThat(e => e.classList.contains("text-box"));

            if (closestTextBox === null) {
                return;
            }

            const activeId = Array.from(closestTextBox.classList)
                .filter(clazz => clazz.toString().match(/text-box-\d/))
                .getFirst()
                ?.slice(-1)
                ?.toInt();
            if (isDefined(activeId)) {
                await this.scrollToPortrait(activeId);
            }
        }
    };

    private async waitForBrowserToFocusNextElement(): Promise<void> {
        await waitTime(5); // Didn't find a better way than waiting. 0ms was actually not enough when testing on Firefox, 1ms was. 5ms to be "more" sure.
    }

    private updateScrollState(): void {
        if (this.isOnMobile()) {
            return;
        }

        const moduleScrolledRelative = this.moduleScrolledRelative();

        this.style.setProperty("--relative-scroll", "" + moduleScrolledRelative);
        for (let i = 0; i < this.config.portraitCount; i++) {
            if (i > 0 || this.config.hasGroupShot) {
                const isInCarousel = moduleScrolledRelative < this.config.portraitAnimationStartTimeline[i];
                this.portraits.item(i).classList.toggle("in-carousel", isInCarousel);
            }

            const [start, end] = this.config.portraitTextBoxVisibilityIntervalTimeline[i];
            const isVisible = start <= moduleScrolledRelative && moduleScrolledRelative <= end;
            this.textBoxes.item(i).classList.toggle("visible", isVisible);
        }
    }

    private moduleScrolledRelative(): number {
        const moduleScrolledPixels = -this.getBoundingClientRect().top;
        const moduleScrolledRelative = moduleScrolledPixels / this.scrollInterval();
        return clamp(moduleScrolledRelative, 0, 1);
    }

    private async scrollToPortrait(id: number): Promise<void> {
        const moduleScrolledRelative = this.config.portraitOnStageTimeline[id - 1];
        const scrollInterval = this.scrollInterval();
        const moduleScrolledPixels = moduleScrolledRelative * scrollInterval;

        this.classList.add("turbo-scrolling");
        await this.scrollService.scrollToElement(this, -moduleScrolledPixels, 500, 1000);
        this.classList.remove("turbo-scrolling");
    }

    private scrollInterval(): number {
        return this.config.scrollHeight - this.contentArea.clientHeight;
    }

    private isOnMobile(): boolean {
        return this.resolution.upTo(BOOTSTRAP.SM);
    }
}
